import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import { AuthInfo } from "users/auth/info/auth-info";
import type { AuthResponse } from "users/_api/auth-email-api-service";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import SystemNotificationAPIResponse from "users/super-users/_api/responses/system-notification-api-response";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import {
  type UserEntityMemberInvitationListQuery,
  userEntityMemberInvitationListParams,
} from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import SystemNotification from "notifications/system-notifications/system-notification";
import {
  parseSystemMessageSeverity,
  SystemMessageSeverity,
} from "notifications/system-notifications/system-message-severity";
import UserInformationSchema, { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { HttpStatusCode } from "axios";
import z from "zod";

const superUserListTag = { type: "UserInformation" as const, id: "SUPER_USER_LIST" };
const systemNotificationsTag = { type: "SystemNotification" as const, id: "LIST" };
const activeInvitationsTag = { type: "AdministratorInvitation" as const, id: "ACTIVE_USER_LIST" };
const systemNotificationResponseSchema = z.object({
  id: z.uuid(),
  content: z.string(),
  message: z.string().optional(),
  severity: z.string().optional(),
  createdDate: z.string().optional(),
});
type SystemNotificationResponseJSON = z.infer<typeof systemNotificationResponseSchema>;
export type OnboardingWorkStaffResolutionResponse = {
  requestId: string;
  state: string;
  currentProposalId: string;
  currentNegotiationId: string;
  relatedProposalIds: string[];
  closedByUserId?: string | null;
  closedDate?: string | null;
  closureReason?: string | null;
};
export type ClientRepresentativeChoice = {
  id: string;
  userId: Guid;
  legalEntityId: Guid;
  firstName: string;
  lastName: string;
  companyName: string;
};
type ClientRepresentativeChoiceJSON = {
  userId: string;
  legalEntityId: string;
  firstName: string;
  lastName: string;
  companyName: string;
};
const clientRepresentativeChoiceSchema: z.ZodType<ClientRepresentativeChoiceJSON> = z.object({
  userId: z.uuid(),
  legalEntityId: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  companyName: z.string(),
});
const onboardingWorkStaffResolutionResponseSchema: z.ZodType<OnboardingWorkStaffResolutionResponse> = z.object({
  requestId: z.uuid(),
  state: z.string().min(1),
  currentProposalId: z.uuid(),
  currentNegotiationId: z.uuid(),
  relatedProposalIds: z.array(z.uuid()),
  closedByUserId: z.uuid().nullish(),
  closedDate: z.string().nullish(),
  closureReason: z.string().nullish(),
});

/** An organisation with at least one active member invitation: one choice of the list's organisation filter. */
export type ActiveInvitationOrganisation = {
  legalEntityId: Guid;
  legalEntityName: string;
};
const activeInvitationOrganisationSchema = z.object({
  legalEntityId: z.uuid(),
  legalEntityName: z.string(),
});
type ActiveInvitationOrganisationJSON = z.infer<typeof activeInvitationOrganisationSchema>;

/**
 * One page of every organisation's active member invitations (#5447). The search also matches the
 * organisation's name while no organisation is chosen.
 */
export type ActiveUserInvitationsQuery = UserEntityMemberInvitationListQuery & {
  /**
   * Only this organisation's invitations, by its id's value. Absent lists every organisation's.
   *
   * A string, not a `Guid`: RTK Query keeps only plain values stable between renders, so a `Guid`
   * built during render would send a failed read again on every render (#6488).
   */
  legalEntityId?: string;
};

function activeUserInvitationsParams({ legalEntityId, ...query }: ActiveUserInvitationsQuery): URLSearchParams {
  const params = userEntityMemberInvitationListParams(query);
  if (legalEntityId) params.set("legalEntityId", legalEntityId);
  return params;
}

export const superUserApi = api.injectEndpoints({
  endpoints: (build) => ({
    registerSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/register",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    unregisterSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/unregister",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    replaceOnboardingProposal: build.mutation<OnboardingWorkStaffResolutionResponse, { requestId: string; templateId: string }>({
      query: ({ requestId, templateId }) => ({
        url: `work/onboarding/${requestId}/replace-proposal`,
        method: "POST",
        body: JSON.stringify({ templateId }),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: onboardingWorkStaffResolutionResponseSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    retryOnboardingProvisioning: build.mutation<OnboardingWorkStaffResolutionResponse, { requestId: string }>({
      query: ({ requestId }) => ({
        url: `work/onboarding/${requestId}/retry-provisioning`,
        method: "POST",
      }),
      rawResponseSchema: onboardingWorkStaffResolutionResponseSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    closeOnboarding: build.mutation<OnboardingWorkStaffResolutionResponse, { requestId: string; reason: string }>({
      query: ({ requestId, reason }) => ({
        url: `work/onboarding/${requestId}/close`,
        method: "POST",
        body: JSON.stringify({ reason }),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: onboardingWorkStaffResolutionResponseSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    disableSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/disable-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    enableSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/enable-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    loginAsUser: build.mutation<AuthResponse, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/login-as-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: AuthInfoSchema.AuthInfoJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown) => AuthInfo.parseAuthResponse(response),
      transformErrorResponse: toSuperUserApiError,
    }),
    createSystemNotification: build.mutation<
      void,
      { notificationType: SystemMessageSeverity; notificationContent: string }
    >({
      query: ({ notificationType, notificationContent }) => ({
        url: "super-user/system-notifications",
        method: "POST",
        body: JSON.stringify({ message: notificationContent, severity: notificationType }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    updateSystemNotification: build.mutation<
      void,
      { notificationId: Guid; notificationType: SystemMessageSeverity; notificationContent: string }
    >({
      query: ({ notificationId, notificationType, notificationContent }) => ({
        url: `super-user/system-notifications/${notificationId.value}`,
        method: "PUT",
        body: JSON.stringify({ message: notificationContent, severity: notificationType }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    deleteSystemNotification: build.mutation<void, { notificationId: Guid }>({
      query: ({ notificationId }) => ({ url: `super-user/system-notifications/${notificationId.value}`, method: "DELETE" }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    searchUsers: build.query<PaginatedResponse<UserInformation>, { request: SearchUsersAPIRequest }>({
      query: ({ request }) => ({ url: "super-user/search-users", params: searchUsersParams(request) }),
      rawResponseSchema: z.array(UserInformationSchema.UserInformationJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: UserInformationJSON[], meta) =>
        PaginatedResponse.from(
          response.map((item) => UserInformation.fromJSON(item)).filter((item): item is UserInformation => Boolean(item)),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      transformErrorResponse: toSuperUserApiError,
      providesTags: [superUserListTag],
    }),
    searchClientRepresentatives: build.query<
      PaginatedResponse<ClientRepresentativeChoice>,
      { request: SearchUsersAPIRequest }
    >({
      query: ({ request }) => ({
        url: "super-user/client-representatives",
        params: searchUsersParams(request),
      }),
      rawResponseSchema: z.array(clientRepresentativeChoiceSchema),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: ClientRepresentativeChoiceJSON[], meta) =>
        PaginatedResponse.from(
          response.map((choice) => ({
            id: `${choice.userId}:${choice.legalEntityId}`,
            userId: new Guid(choice.userId),
            legalEntityId: new Guid(choice.legalEntityId),
            firstName: choice.firstName,
            lastName: choice.lastName,
            companyName: choice.companyName,
          })),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      transformErrorResponse: toSuperUserApiError,
      providesTags: [superUserListTag],
    }),
    getAppVersion: build.query<string, void>({
      query: () => "super-user/version",
      rawResponseSchema: z.string(),
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: (response, meta) => {
        if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) {
          return new VersionInfoNotAvailableError("Unable to fetch version info, caller must be a super-user");
        }
        return toSuperUserApiError(response, meta);
      },
    }),
    getSystemNotifications: build.query<PaginatedResponse<SystemNotification>, SystemNotificationsQuery>({
      query: (request) => ({ url: "super-user/system-notifications", params: systemNotificationsParams(request) }),
      rawResponseSchema: z.array(systemNotificationResponseSchema),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: SystemNotificationResponseJSON[], meta) =>
        PaginatedResponse.from(
          response.map((item) =>
            new SystemNotificationAPIResponse({
              ...item,
              severity: parseSystemMessageSeverity(item.severity),
            }).deserialize(),
          ),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      // A 403 stays a refusal: the table reports it as refused, never as nothing to fetch (#6490).
      transformErrorResponse: toSuperUserApiError,
      providesTags: [systemNotificationsTag],
    }),
    getActiveUserInvitations: build.query<PaginatedResponse<UserEntityMemberInvitation>, ActiveUserInvitationsQuery>({
      query: (request) => ({ url: "super-user/active-user-invitations", params: activeUserInvitationsParams(request) }),
      rawResponseSchema: z.array(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ),
      onSchemaFailure: logSchemaFailure,
      // The page is the body; every total is in X-Pagination (#5447).
      transformResponse: (response: UserEntityMemberInvitationJSON[], meta) =>
        PaginatedResponse.from(
          UserEntityMemberInvitation.fromJSONArray(response),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      transformErrorResponse: (response, meta) => {
        if (getApiErrorStatus(response) === HttpStatusCode.NotFound) return new UserEntityMemberInvitationsNotFoundError();
        return toSuperUserApiError(response, meta);
      },
      providesTags: [activeInvitationsTag],
    }),
    getActiveUserInvitationOrganisations: build.query<ActiveInvitationOrganisation[], void>({
      query: () => "super-user/active-user-invitations/organisations",
      rawResponseSchema: z.array(activeInvitationOrganisationSchema),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: ActiveInvitationOrganisationJSON[]) =>
        response.map((organisation) => ({
          legalEntityId: new Guid(organisation.legalEntityId),
          legalEntityName: organisation.legalEntityName,
        })),
      transformErrorResponse: toSuperUserApiError,
      providesTags: [activeInvitationsTag],
    }),
  }),
});

export const {
  useRegisterSuperUserMutation,
  useUnregisterSuperUserMutation,
  useReplaceOnboardingProposalMutation,
  useCloseOnboardingMutation,
  useRetryOnboardingProvisioningMutation,
  useDisableSuperUserMutation,
  useEnableSuperUserMutation,
  useLoginAsUserMutation,
  useCreateSystemNotificationMutation,
  useUpdateSystemNotificationMutation,
  useDeleteSystemNotificationMutation,
  useSearchUsersQuery,
  useSearchClientRepresentativesQuery,
  useGetAppVersionQuery,
  useGetSystemNotificationsQuery,
  useGetActiveUserInvitationsQuery,
  useGetActiveUserInvitationOrganisationsQuery,
} = superUserApi;

export class SuperUserAPIError extends Error {
  constructor(method: string | undefined, public readonly error: unknown) {
    const endpointName = method ? `.${method}` : "";
    super(`The administrator request${endpointName} could not be completed.`);
  }
}

export class UserEntityMemberInvitationsNotFoundError extends Error {
  constructor() {
    super("Unable to retrieve open user entity member invitations");
  }
}

export class VersionInfoNotAvailableError extends Error {}

function toSuperUserApiError(response: FetchBaseQueryError, meta?: unknown): SuperUserAPIError {
  return new SuperUserAPIError(getApiEndpointName(meta), response);
}

/**
 * One page of the super-user system notification list. The API orders it by created date,
 * newest first unless `orderDir` says otherwise (GitHub issue #5445).
 */
export type SystemNotificationsQuery = {
  pagination: PaginationParameters;
  query?: string;
  orderDir?: OrderDirection;
};

function systemNotificationsParams(request: SystemNotificationsQuery): URLSearchParams {
  const params = request.pagination.searchParams;
  const query = request.query?.trim();
  if (query) params.set("query", query);
  params.set("orderBy", "CreatedDate");
  params.set("orderDir", request.orderDir ?? OrderDirection.Descending);
  return params;
}

function searchUsersParams(request: SearchUsersAPIRequest): URLSearchParams {
  const params = request.searchParams;
  if (request.query) params.set("query", request.query);
  if (request.sort) params.set("sort", request.sort);
  for (const [field, value] of Object.entries(request.filters)) params.set(field, value);
  return params;
}
